import { useCallback } from "react";
import { useIntl } from "react-intl";
import { AgentInteractionStatus } from "@/common/constants/callSession";
import { useMessage } from "@/common/messages";
import { AgentPermissionsState } from "@/common/stores/persistingStores/agentPermissions";
import { useAgentPermissionsStore } from "@/common/stores/persistingStores/agentPermissions/agentPermissionsStore";
import {
	CallSessionState,
	useCallSessionStore,
} from "@/common/stores/nonpersistingStores/callSession";
import {
	MessagingInviteState,
	useMessagingInviteSessionStore,
} from "@/common/stores/nonpersistingStores/messagingInvite";
import {
	OutboundCallRequest,
	OutboundCallResponse,
	TrustedEndpoint,
} from "@/repository/models/outboundCall";
import {
	VoiceEndpointsKey,
	fetchVoiceTrustedEndpoints,
	outboundCall,
} from "@/repository/queryFns";
import { useQuery } from "@tanstack/react-query";
import { useAcs } from "@/common/acs/AcsContext";
import {
	getRingingFilePath,
	ringingFilePathQueryKey,
} from "@/repository/queryFns/utils";
import {
	QueuesState,
	useQueuesStore,
} from "@/common/stores/nonpersistingStores/queues";
import { AcsClientType, PlatformType } from "@/common/constants/shared";
import { useAppSettingsStore } from "@/common/stores/persistingStores/app/appSettingsStore";
import {
	AppCacheState,
	AppSettingsState,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";
import {
	CurrentInteractionState,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";

export interface OutboundVoiceData {
	isOutboundPossible: boolean;
	isOutboundAllowed: boolean;
	trustedEndpoints: TrustedEndpoint[];
	initCallAction: (date: OutboundCallRequest) => Promise<OutboundCallResponse>;
	hasAnyOutboundRightEndpoints: boolean;
	isUnifyInteractionOnHold: boolean;
}

export const useVoiceOutbound = (): OutboundVoiceData => {
	const intl = useIntl();
	const { show } = useMessage();
	const hasOutboundRights = useAgentPermissionsStore(
		(state: AgentPermissionsState) => state.hasOutboundRights
	);
	const callSession = useCallSessionStore(
		(state: CallSessionState) => state.callSession
	);
	const messagingInviteSession = useMessagingInviteSessionStore(
		(state: MessagingInviteState) => state.messagingSession
	);

	const appSettings = useAppSettingsStore(
		(state: AppSettingsState) => state.settings
	);

	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);

	const appStoreCache = useAppCacheStore((state: AppCacheState) => state.cache);

	const { data: ringingFilePath } = useQuery<string>({
		queryKey: [ringingFilePathQueryKey, false],
		queryFn: () => getRingingFilePath(false),
	});

	const endpointsResult = useQuery({
		queryKey: [VoiceEndpointsKey],
		queryFn: fetchVoiceTrustedEndpoints,
	});

	const queues = useQueuesStore((state: QueuesState) => state.queues);

	const { startCall } = useAcs();

	const initCallAction = useCallback(async (data: OutboundCallRequest) => {
		data.scenarioId = crypto.randomUUID();

		let message = "OutboundSelfRinging";

		if (data.selectedEndpoint.phoneNumber === data.target.target) {
			show("workspace", {
				text: intl.formatMessage({ id: `Interaction.New.OutboundSelfRinging` }),
				intent: "error",
				duration: 3000,
			});

			throw new Error(message);
		}

		const result = await outboundCall(data);

		if (result.contactNotFound || result.isSelfRinging) {
			if (result.contactNotFound) {
				message = "ContactNotFound";
			}

			show("workspace", {
				text: intl.formatMessage({ id: `Interaction.New.${message}` }),
				intent: "error",
				duration: 3000,
			});

			throw new Error(message);
		}

		show("workspace", {
			text: intl.formatMessage({ id: "Interaction.New.CallInitiated" }),
			intent: "success",
			duration: 3000,
		});

		if (data.serverSideCall) {
			return result;
		}

		data.ringingFilePath = ringingFilePath;
		data.target.contactId = result.contactId;
		data.target.contactName = result.contactName;
		data.isAgent = result.isAgent;

		// emergency call handling, a callback pstn call will be received later
		if (data.target.target === "933") {
			if (data.target.contactName === null) {
				data.target.contactName = intl.formatMessage({
					id: "Interaction.New.Emergency",
				});
			}
		}

		await startCall(data);

		return result;
	}, []);

	const outboundRightQueueIds = queues
		.filter((x) => x.hasOutboundRight)
		.map((x) => x.id);

	const outboundRightTrustedEndpoints = endpointsResult.data?.filter((x) =>
		outboundRightQueueIds.includes(x.queueId)
	);

	const isCallInProgress = appStoreCache[CacheKey.CallInProgress];

	// During Offering/SimultaneousOffering/warm-transfer-invitation the session exists but
	// CallInProgress is false, so the checks below would otherwise allow outbound while a call
	// is ringing. Mirror the messaging-invite offering guard so every consumer stays consistent.
	const isCallOffering =
		callSession?.state === AgentInteractionStatus.Offering ||
		callSession?.state === AgentInteractionStatus.SimultaneousOffering ||
		callSession?.state ===
			AgentInteractionStatus.ReceivingWarmTransferInvitation;

	const isOutboundAllowed =
		!isCallOffering &&
		(!callSession ||
			!isCallInProgress ||
			callSession.state === AgentInteractionStatus.Terminated ||
			(callSession.platformType === PlatformType.Acs &&
				appSettings.acsClientType !== AcsClientType.Teams &&
				appStoreCache[CacheKey.OnHoldStatus])) &&
		(!messagingInviteSession ||
			messagingInviteSession.state !== AgentInteractionStatus.Offering);

	const isOutboundPossible =
		hasOutboundRights && !!outboundRightTrustedEndpoints?.length;

	const hasAnyOutboundRightEndpoints =
		(outboundRightTrustedEndpoints?.length ?? 0) > 0;

	const isUnifyInteractionOnHold =
		(appStoreCache[CacheKey.OnHoldStatus] && currentInteraction?.isUnify) ??
		false;

	return {
		isOutboundPossible,
		isOutboundAllowed,
		trustedEndpoints: outboundRightTrustedEndpoints || [],
		initCallAction,
		hasAnyOutboundRightEndpoints,
		isUnifyInteractionOnHold,
	};
};
